import { NavigationExtras, Router, RouteReuseStrategy } from '@angular/router';

/**
 * Navigate while forcing the destination to be destroyed and re-created, WITHOUT
 * leaving route reuse switched off for the rest of the session.
 *
 * Background — several places used to do
 *   `router.routeReuseStrategy.shouldReuseRoute = () => false`
 * inline and never put it back. That flag is global, so from that moment on EVERY
 * navigation in the app tore down and rebuilt the whole layout (nav bar, banners,
 * side menus). Visible symptom: the business page's Employees / Followers arrows
 * stopped expanding, because the menu component was recreated mid-click and its
 * open/closed flags reset. It only reproduced after the user had passed through
 * one of the leaking screens, which is why it looked random.
 *
 * This helper keeps the "reload the destination" behaviour those callers wanted,
 * but scopes it to the single navigation: capture the pristine strategy, disable
 * reuse, navigate, and restore once the navigation settles (success, cancel or
 * error). A depth counter handles overlapping calls so the first caller's
 * pristine value is the one restored.
 */

let overrideDepth = 0;
let savedShouldReuse: RouteReuseStrategy['shouldReuseRoute'] | null = null;
let savedOnSameUrl: Router['onSameUrlNavigation'] | null = null;

export function navigateWithFreshRoute(
  router: Router,
  commands: any[],
  extras: NavigationExtras = {}
): Promise<boolean> {
  const strategy = router.routeReuseStrategy;

  if (overrideDepth === 0) {
    savedShouldReuse = strategy.shouldReuseRoute;
    savedOnSameUrl = router.onSameUrlNavigation;
    strategy.shouldReuseRoute = () => false;
    router.onSameUrlNavigation = 'reload';
  }
  overrideDepth++;

  const restore = () => {
    overrideDepth = Math.max(0, overrideDepth - 1);
    if (overrideDepth === 0 && savedShouldReuse) {
      strategy.shouldReuseRoute = savedShouldReuse;
      router.onSameUrlNavigation = savedOnSameUrl ?? 'ignore';
      savedShouldReuse = null;
      savedOnSameUrl = null;
    }
  };

  let navigation: Promise<boolean>;
  try {
    navigation = router.navigate(commands, { onSameUrlNavigation: 'reload', ...extras });
  } catch (e) {
    restore();
    throw e;
  }
  return navigation.then(
    result => { restore(); return result; },
    error => { restore(); throw error; }
  );
}
